import React, { useRef } from 'react';
import { Textarea, Button, Select, SelectItem } from '@heroui/react';
import { MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline';
import AITooltip from './AITooltip';
import { useAppContext } from '../contexts/AppContext';
import { useAppHandlers } from '../hooks/useAppHandlers';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { useRewriteFeature } from '../hooks/useRewriteFeature';

const EditorSection = ({ textareaRef }) => {
  const containerRef = useRef(null);
  const handleRef = useRef(null);

  const { state, actions } = useAppContext();
  const { reportText, ghostText, fontSize, editorWidth } = state;
  const { setFontSize } = actions;

  const { handleTextareaChange } = useAppHandlers();
  const { isListening, toggleVoiceInput } = useVoiceInput();
  const { handleMouseDown, handleKeyDown } = useRewriteFeature(textareaRef);

  const fontSizeClasses = {
    'small': 'text-sm',
    'normal': 'text-base',
    'large': 'text-lg',
    'x-large': 'text-xl'
  };

  return (
    <div
      ref={containerRef}
      className="relative flex-1 h-full"
      style={{ width: `${editorWidth}%` }}
    >
      {/* Editor Controls */}
      <div className="flex items-center justify-between mb-4 bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <Select
            label="Font Size"
            selectedKeys={[fontSize]}
            onSelectionChange={(keys) => setFontSize(Array.from(keys)[0])}
            className="w-32"
            variant="bordered"
            size="sm"
          >
            <SelectItem key="small">Small</SelectItem>
            <SelectItem key="normal">Normal</SelectItem>
            <SelectItem key="large">Large</SelectItem>
            <SelectItem key="x-large">X-Large</SelectItem>
          </Select>
          
          <Button
            color={isListening ? "danger" : "primary"}
            variant="flat"
            startContent={isListening ? <StopIcon className="h-4 w-4" /> : <MicrophoneIcon className="h-4 w-4" />}
            onPress={toggleVoiceInput}
            size="sm"
          >
            {isListening ? 'Stop' : 'Voice Input'}
          </Button>
        </div>
        
      </div>

      {/* Main Editor */}
      <div className="relative h-full">
        <Textarea
          ref={textareaRef}
          placeholder="Start writing your report here..."
          value={reportText}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onMouseDown={handleMouseDown}
          className="w-full h-full resize-none bg-white dark:bg-gray-800"
          variant="bordered"
          minRows={20}
          classNames={{
            input: `min-h-[500px] leading-relaxed ${fontSizeClasses[fontSize]}`,
            inputWrapper: "border-gray-200 dark:border-gray-700 shadow-sm"
          }}
        />
        
        {/* Ghost Text Overlay */}
        {ghostText && (
          <div className="absolute inset-0 pointer-events-none">
            <div className={`p-3 ${fontSizeClasses[fontSize]} text-gray-400 whitespace-pre-wrap`}>
              {reportText}
              <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400">
                {ghostText}
              </span>
            </div>
          </div>
        )}
        
        {/* Resize Handle */}
        <div
          ref={handleRef}
          className="absolute top-0 right-0 w-2 h-full cursor-col-resize bg-gray-300 dark:bg-gray-600 opacity-0 hover:opacity-100 transition-opacity duration-200"
          style={{ transform: 'translateX(50%)', zIndex: 10 }}
        />
      </div>
      <AITooltip />
    </div>
  );
};

export default EditorSection; 